import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { API_URL } from '../../config/api';
import {
  fetchCredentialBucket,
  fetchCredentialPinStatus,
  saveCredentialBucket,
  setCredentialPin,
} from '../../features/notes/services/noteService';
import { addNotification } from '../../store/slices/uiSlice';
import { addMessage } from '../../store/slices/chatSlice';
import {
  downloadAttachment,
  formatFileSize,
  isDisplayableImage,
  isRichTextMessageContent,
  normalizeMessageContentHtml,
  openAttachmentFallback,
} from '../../utils/fileUtils';
import MessageMentionContent from '../common/MessageMentionContent';
import PresignedImage from '../common/PresignedImage';
import FileTypeIcon from '../common/FileTypeIcon';

const pinDigits = (value) => String(value || '').replace(/\D/g, '').slice(0, 8);

const pinInputClass =
  'w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-3 pr-9 py-2 text-sm tracking-[0.3em] text-gray-900 dark:text-white';

const EyeIcon = ({ off }) => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    {off ? (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.6 10.6A2 2 0 0012 14a2 2 0 001.4-.6M9.9 5.1A9.8 9.8 0 0112 5c5 0 9.3 3.1 11 7.5a11.6 11.6 0 01-4.2 5.1M6.1 6.1A11.5 11.5 0 001 12.5C2.7 16.9 7 20 12 20c1.4 0 2.7-.2 3.9-.7" />
      </>
    ) : (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" strokeWidth={2} />
      </>
    )}
  </svg>
);

const SecretField = ({
  value,
  onChange,
  placeholder,
  visible,
  onToggleVisible,
  className = '',
  inputMode,
  autoComplete = 'off',
  autoFocus,
  onKeyDown,
  tracking = false,
}) => (
  <div className={`relative min-w-0 ${className}`}>
    <input
      type={visible ? 'text' : 'password'}
      inputMode={inputMode}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className={tracking ? pinInputClass : 'w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-3 pr-9 py-2 text-sm text-gray-900 dark:text-white'}
    />
    <button
      type="button"
      onClick={onToggleVisible}
      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      aria-label={visible ? 'Hide value' : 'Show value'}
    >
      <EyeIcon off={visible} />
    </button>
  </div>
);

export const unlockSensitiveMessage = async ({ messageId, method, pin }) => {
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${API_URL}/messages/${messageId}/unlock`,
    { method, pin },
    { headers: { Authorization: token ? `Bearer ${token}` : '' } },
  );
  return response.data;
};

export const sensitiveAuthOptions = (msg) => {
  const allowReceiverPin = msg?.allowReceiverPin !== false;
  const hasSenderPin = Boolean(msg?.hasSenderPin);
  return { allowReceiverPin, hasSenderPin };
};

export const sensitiveAuthLine = (msg) => {
  const { allowReceiverPin, hasSenderPin } = sensitiveAuthOptions(msg);
  if (hasSenderPin && !allowReceiverPin) return 'Sender PIN — opens with the PIN shared by the sender';
  if (allowReceiverPin && !hasSenderPin) return 'Receiver PIN — opens with your own PIN';
  if (hasSenderPin) return 'Sender PIN — opens with the PIN shared by the sender';
  return 'Receiver PIN — opens with your own PIN';
};

export const sensitiveAuthLines = (msg) => [sensitiveAuthLine(msg)];

const newCredId = () => `entry-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const SensitiveUnlockedAttachments = ({ attachments, onDownload }) => {
  const items = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
  if (!items.length) return null;
  return (
    <div className="mt-3 space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Attachments</p>
      <div className="grid grid-cols-2 gap-2">
        {items.map((item, index) => {
          const key = item.id || item.fileUrl || `${item.fileName}-${index}`;
          if (isDisplayableImage(item) && item.fileUrl) {
            return (
              <div
                key={key}
                className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-left"
              >
                <PresignedImage
                  fileKey={item.fileUrl}
                  alt={item.fileName || 'Image'}
                  className="h-28 w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => onDownload(item)}
                  className="w-full truncate px-2 py-1.5 text-[11px] font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Download {item.fileName || 'image'}
                </button>
              </div>
            );
          }
          return (
            <button
              key={key}
              type="button"
              onClick={() => onDownload(item)}
              className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-2 text-left"
            >
              <FileTypeIcon fileName={item.fileName} fileType={item.fileType} size="sm" />
              <span className="min-w-0">
                <span className="block truncate text-[12px] font-medium text-gray-900 dark:text-white">
                  {item.fileName || 'File'}
                </span>
                <span className="block text-[10px] text-primary-600 dark:text-primary-400 font-semibold">
                  Download{formatFileSize(item.fileSize) ? ` · ${formatFileSize(item.fileSize)}` : ''}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const emptyCredRow = (key = '') => ({
  id: newCredId(),
  key,
  value: '',
  valueVisible: false,
});

const stripCredentialPlainText = (raw) => {
  const html = String(raw || '');
  if (!html) return '';
  return html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\u00a0/g, ' ')
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
};

const parseCredentialPairs = (raw) => {
  const pairs = [];
  const seen = new Set();
  const addPair = (key, value) => {
    const nextKey = String(key || '').trim();
    const nextValue = String(value || '').trim();
    if (!nextKey || !nextValue) return;
    const id = nextKey.toLowerCase();
    if (seen.has(id)) return;
    seen.add(id);
    pairs.push({ key: nextKey, value: nextValue });
  };

  stripCredentialPlainText(raw).split(/\r?\n/).forEach((line) => {
    const colonChunks = line.split(/\s+(?=[^:]{1,40}:\s*\S)/);
    (colonChunks.length ? colonChunks : [line]).forEach((chunk) => {
      const colon = chunk.match(/^(.+?)\s*:\s*(.+)$/);
      const dash = !colon && chunk.match(/^(.+?)\s+-\s+(.+)$/);
      const pair = colon || dash;
      if (pair) addPair(pair[1], pair[2]);
    });
  });
  return pairs;
};

const rowsFromCredentialPairs = (pairs) => {
  const usernameKey = /^(user(name)?|login|email)$/i;
  const passwordKey = /^(pass(word)?|pwd|secret)$/i;
  let username = '';
  let password = '';
  const extra = [];
  pairs.forEach((pair) => {
    if (!username && usernameKey.test(pair.key)) username = pair.value;
    else if (!password && passwordKey.test(pair.key)) password = pair.value;
    else extra.push(pair);
  });
  return [
    { ...emptyCredRow('username'), value: username },
    { ...emptyCredRow('password'), value: password },
    ...extra.map((pair) => ({ ...emptyCredRow(pair.key), value: pair.value })),
  ];
};

const ModalShell = ({ title, children, onClose, wide }) => {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className={`w-full ${wide ? 'max-w-lg' : 'max-w-sm'} rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl`}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

const AuthChoice = ({ selected, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`w-full rounded-xl border px-3 py-2.5 text-left transition-colors ${
      selected
        ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/40'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
    }`}
  >
    <span className="flex items-center justify-between gap-2">
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{title}</span>
      <span
        className={`flex h-4 w-4 items-center justify-center rounded-full border ${
          selected ? 'border-rose-600' : 'border-gray-300 dark:border-gray-500'
        }`}
      >
        {selected ? <span className="h-2 w-2 rounded-full bg-rose-600" /> : null}
      </span>
    </span>
    <span className="mt-1 block text-[11px] leading-snug text-gray-500 dark:text-gray-400">
      {description}
    </span>
  </button>
);

export const SensitiveSendModal = ({ open, onClose, onConfirm }) => {
  const [authMethod, setAuthMethod] = useState('receiver');
  const [title, setTitle] = useState('');
  const [senderPin, setSenderPin] = useState('');
  const [confirmSenderPin, setConfirmSenderPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showSenderPin, setShowSenderPin] = useState(false);
  const [showConfirmSenderPin, setShowConfirmSenderPin] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAuthMethod('receiver');
    setTitle('');
    setSenderPin('');
    setConfirmSenderPin('');
    setShowSenderPin(false);
    setShowConfirmSenderPin(false);
    setBusy(false);
    setError('');
  }, [open]);

  if (!open) return null;

  const titleOk = title.trim().length > 0;
  const useSenderPin = authMethod === 'sender';
  const senderPinOk = senderPin.length >= 4 && senderPin.length <= 8 && senderPin === confirmSenderPin;
  const canSubmit = !busy && titleOk && (!useSenderPin || senderPinOk);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      onConfirm?.({
        sensitiveTitle: title.trim(),
        allowReceiverPin: authMethod === 'receiver',
        senderPin: useSenderPin ? senderPin : undefined,
      });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not continue');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Choose unlock method" onClose={onClose}>
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">Protect this message</h2>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        Add a title (shown on the locked message) and choose one unlock method.
      </p>
      <label className="mt-4 block text-xs font-medium text-gray-700 dark:text-gray-300">
        Title
      </label>
      <input
        type="text"
        value={title}
        onChange={(event) => setTitle(event.target.value.slice(0, 80))}
        placeholder="Visible on the locked message"
        className="mt-2 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
        autoFocus
      />
      <p className="mt-3 text-xs font-medium text-gray-700 dark:text-gray-300">Unlock with</p>
      <div className="mt-2 space-y-2">
        <AuthChoice
          selected={authMethod === 'receiver'}
          title="Receiver PIN"
          description="Opens using the receiver’s own PIN (same PIN they use in Notes)."
          onClick={() => setAuthMethod('receiver')}
        />
        <AuthChoice
          selected={authMethod === 'sender'}
          title="Sender PIN"
          description="Opens only with a PIN you share separately with the receiver."
          onClick={() => setAuthMethod('sender')}
        />
      </div>
      {useSenderPin ? (
        <>
          <label className="mt-4 block text-xs font-medium text-gray-700 dark:text-gray-300">
            Set sender PIN
          </label>
          <SecretField
            className="mt-2"
            tracking
            inputMode="numeric"
            value={senderPin}
            visible={showSenderPin}
            onToggleVisible={() => setShowSenderPin((open) => !open)}
            onChange={(event) => setSenderPin(pinDigits(event.target.value))}
            placeholder="4–8 digits"
          />
          <SecretField
            className="mt-2"
            tracking
            inputMode="numeric"
            value={confirmSenderPin}
            visible={showConfirmSenderPin}
            onToggleVisible={() => setShowConfirmSenderPin((open) => !open)}
            onChange={(event) => setConfirmSenderPin(pinDigits(event.target.value))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
            placeholder="Confirm sender PIN"
          />
        </>
      ) : null}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={submit}
          className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Sending…' : titleOk ? 'Send protected' : 'Title required'}
        </button>
      </div>
    </ModalShell>
  );
};

export const SensitiveUnlockModal = ({ open, message, onClose }) => {
  const dispatch = useDispatch();
  const { allowReceiverPin, hasSenderPin } = sensitiveAuthOptions(message);
  const defaultMethod = allowReceiverPin ? 'USER' : 'SENDER';
  const [method, setMethod] = useState(defaultMethod);
  const [pinEnabled, setPinEnabled] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [unlocked, setUnlocked] = useState(null);
  const [savingCredential, setSavingCredential] = useState(false);
  const [savedCredential, setSavedCredential] = useState(false);
  const [credentialFormOpen, setCredentialFormOpen] = useState(false);
  const [credTitle, setCredTitle] = useState('');
  const [credRows, setCredRows] = useState([emptyCredRow('username'), emptyCredRow('password')]);
  const [credAskPinOnCopy, setCredAskPinOnCopy] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMethod(message?.allowReceiverPin === false ? 'SENDER' : 'USER');
    setPin('');
    setConfirmPin('');
    setShowPin(false);
    setShowConfirmPin(false);
    setBusy(false);
    setError('');
    setUnlocked(null);
    setSavingCredential(false);
    setSavedCredential(false);
    setCredentialFormOpen(false);
    setCredTitle('');
    setCredAskPinOnCopy(false);
    setCredRows([emptyCredRow('username'), emptyCredRow('password')]);
    fetchCredentialPinStatus()
      .then((status) => setPinEnabled(Boolean(status?.enabled)))
      .catch(() => setPinEnabled(false));
  }, [open, message?.id, message?.allowReceiverPin]);

  if (!open || !message) return null;

  const pinOk = pin.length >= 4 && pin.length <= 8;
  const needsSetPin = method === 'USER' && !pinEnabled;
  const canSubmit = !busy && pinOk && (!needsSetPin || pin === confirmPin);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      if (needsSetPin) {
        await setCredentialPin({ pin });
        setPinEnabled(true);
      }
      const data = await unlockSensitiveMessage({
        messageId: message.id,
        method,
        pin,
      });
      const attachments = (Array.isArray(data?.attachments) ? data.attachments : []).map((item) => ({
        ...item,
        fileUrl: item.fileUrl || item.url || item.fileKey,
        fileName: item.fileName || item.name,
      }));
      setUnlocked({ ...data, attachments });
      if (message.channelId && message.id) {
        dispatch(addMessage({
          channelId: message.channelId,
          skipUnreadCountIncrement: true,
          message: {
            id: message.id,
            channelId: message.channelId,
            attachments,
            attachmentGroupName: data?.attachmentGroupName,
          },
        }));
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Unlock failed');
    } finally {
      setBusy(false);
    }
  };

  const content = String(unlocked?.content || '');
  const isRich = isRichTextMessageContent(content);

  const openCredentialForm = () => {
    if (savedCredential) return;
    setCredTitle(String(message.sensitiveTitle || '').trim().slice(0, 80));
    const pairs = parseCredentialPairs(unlocked?.content || '');
    setCredRows(pairs.length
      ? rowsFromCredentialPairs(pairs)
      : [emptyCredRow('username'), emptyCredRow('password')]);
    setCredAskPinOnCopy(false);
    setCredentialFormOpen(true);
  };

  const saveCredentialBlock = async () => {
    const title = credTitle.trim().slice(0, 80);
    const entries = credRows
      .map((row, index) => ({
        key: String(row.key || '').trim() || (index === 0 ? 'username' : index === 1 ? 'password' : `item ${index + 1}`),
        value: String(row.value || '').trim(),
        valueVisible: Boolean(row.valueVisible),
      }))
      .filter((row) => row.value);
    if (!title) {
      dispatch(addNotification({ type: 'error', message: 'Enter a credential title' }));
      return;
    }
    if (!entries.length) {
      dispatch(addNotification({ type: 'error', message: 'Enter a username or password' }));
      return;
    }
    setSavingCredential(true);
    try {
      const existing = await fetchCredentialBucket();
      await saveCredentialBucket([
        ...(Array.isArray(existing) ? existing : []),
        {
          title,
          askPinOnCopy: Boolean(pinEnabled && credAskPinOnCopy),
          entries,
        },
      ]);
      window.dispatchEvent(new CustomEvent('workloop:notes-changed'));
      setSavedCredential(true);
      setCredentialFormOpen(false);
      dispatch(addNotification({
        type: 'success',
        message: `Saved “${title}” to credentials`,
      }));
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || err?.message || 'Could not save credential',
      }));
    } finally {
      setSavingCredential(false);
    }
  };

  if (unlocked) {
    return (
      <ModalShell title="Sensitive message" onClose={onClose} wide={credentialFormOpen}>
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {message.sensitiveTitle || 'Sensitive message'}
          </h2>
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">
            Protected
          </span>
        </div>
        <div className="mt-3 max-h-[32vh] overflow-y-auto rounded-xl border border-rose-100 dark:border-rose-900/40 bg-rose-50/60 dark:bg-rose-950/20 p-3 text-sm text-gray-900 dark:text-white">
          {content ? (
            <MessageMentionContent
              isRichText={isRich}
              content={content}
              normalizedContent={normalizeMessageContentHtml(content)}
              className={isRich ? 'ql-editor-content' : 'whitespace-pre-wrap break-words'}
            />
          ) : (
            <p className="text-xs text-gray-500">No text in this protected message.</p>
          )}
        </div>
        <SensitiveUnlockedAttachments
          attachments={unlocked?.attachments}
          onDownload={async (item) => {
            try {
              await downloadAttachment(item);
            } catch (error) {
              try {
                openAttachmentFallback(item);
              } catch {
                dispatch(addNotification({
                  type: 'error',
                  message: error?.message || 'Could not download attachment',
                }));
              }
            }
          }}
        />
        {credentialFormOpen && !savedCredential ? (
          <div className="mt-4">
            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">Save as credential</p>
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              Same as Notes credentials: title, then username and password.
            </p>
            <input
              value={credTitle}
              onChange={(event) => setCredTitle(event.target.value.slice(0, 80))}
              placeholder="title"
              className="mt-3 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
            />
            <div className="mt-2 space-y-2">
              {credRows.map((row) => (
                <div key={row.id} className="flex items-center gap-2">
                  <input
                    value={row.key}
                    onChange={(event) => setCredRows((prev) => prev.map((item) => (
                      item.id === row.id ? { ...item, key: event.target.value } : item
                    )))}
                    placeholder="name"
                    autoComplete="off"
                    className="w-[38%] flex-shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  />
                  <SecretField
                    className="flex-1"
                    value={row.value}
                    visible={Boolean(row.valueVisible)}
                    onToggleVisible={() => setCredRows((prev) => prev.map((item) => (
                      item.id === row.id ? { ...item, valueVisible: !item.valueVisible } : item
                    )))}
                    onChange={(event) => setCredRows((prev) => prev.map((item) => (
                      item.id === row.id ? { ...item, value: event.target.value } : item
                    )))}
                    placeholder={row.key || 'value'}
                  />
                  {credRows.length > 2 ? (
                    <button
                      type="button"
                      onClick={() => setCredRows((prev) => prev.filter((item) => item.id !== row.id))}
                      className="flex-shrink-0 text-gray-400 hover:text-rose-500 px-1 text-lg leading-none"
                      aria-label="Remove row"
                    >
                      ×
                    </button>
                  ) : (
                    <span className="w-5 flex-shrink-0" />
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setCredRows((prev) => [...prev, emptyCredRow('')])}
                className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
              >
                + add
              </button>
            </div>
            {pinEnabled ? (
              <label className="mt-3 flex items-start gap-2 text-[12px] text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={credAskPinOnCopy}
                  onChange={(event) => setCredAskPinOnCopy(event.target.checked)}
                  className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
                />
                <span>Ask PIN during copy</span>
              </label>
            ) : (
              <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
                Set a Notes PIN to require it when copying this credential later.
              </p>
            )}
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          {credentialFormOpen && !savedCredential ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCredentialFormOpen(false)}
                className="rounded-lg px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                Back
              </button>
              <button
                type="button"
                onClick={saveCredentialBlock}
                disabled={savingCredential}
                className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                {savingCredential ? 'Saving…' : 'Save credential'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={openCredentialForm}
              disabled={savedCredential}
              className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-3 py-1.5 text-sm font-medium text-rose-700 dark:text-rose-200 disabled:opacity-60"
            >
              {savedCredential ? 'Saved to credentials' : 'Keep in credential bucket'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white dark:bg-white dark:text-gray-900"
          >
            Close
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title="Enter PIN" onClose={onClose}>
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">
        {message.sensitiveTitle || 'Unlock sensitive message'}
      </h2>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        The sender chose how this message can open:
      </p>
      <ul className="mt-2 space-y-1 text-[11px] text-gray-600 dark:text-gray-300">
        {sensitiveAuthLines(message).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {allowReceiverPin && hasSenderPin ? (
        <div className="mt-3 flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
          <button
            type="button"
            onClick={() => setMethod('USER')}
            className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium ${
              method === 'USER' ? 'bg-rose-600 text-white' : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            Receiver PIN
          </button>
          <button
            type="button"
            onClick={() => setMethod('SENDER')}
            className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium ${
              method === 'SENDER' ? 'bg-rose-600 text-white' : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            Sender PIN
          </button>
        </div>
      ) : (
        <p className="mt-3 text-xs font-medium text-gray-800 dark:text-gray-200">
          {hasSenderPin ? 'Enter the sender PIN they shared with you.' : 'Enter your own PIN to open this.'}
        </p>
      )}
      {needsSetPin ? (
        <p className="mt-3 text-[11px] text-amber-700 dark:text-amber-300">
          You do not have a PIN yet. Set one to unlock with Receiver PIN.
        </p>
      ) : null}
      <SecretField
        className="mt-3"
        tracking
        inputMode="numeric"
        autoFocus
        value={pin}
        visible={showPin}
        onToggleVisible={() => setShowPin((open) => !open)}
        onChange={(event) => setPin(pinDigits(event.target.value))}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !needsSetPin) submit();
        }}
        placeholder={method === 'SENDER' ? 'Sender PIN' : 'Your PIN'}
      />
      {needsSetPin ? (
        <SecretField
          className="mt-2"
          tracking
          inputMode="numeric"
          value={confirmPin}
          visible={showConfirmPin}
          onToggleVisible={() => setShowConfirmPin((open) => !open)}
          onChange={(event) => setConfirmPin(pinDigits(event.target.value))}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
          placeholder="Confirm PIN"
        />
      ) : null}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={submit}
          className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Unlocking…' : needsSetPin ? 'Set PIN and unlock' : 'Unlock'}
        </button>
      </div>
    </ModalShell>
  );
};

export default SensitiveUnlockModal;
